import APIS from 'src/constants/ApiConfig';
import HttpService from 'src/services/HttpService';
import { isArray, isObject, isString } from 'util';
import { message } from 'antd';
import { ICommonResult, IPageResult } from 'src/entity/BaseResult';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import LocationService from './LocationService';
import { IAreaInfo } from 'src/entity/LocationEntity';
import moment from 'moment';

export enum EAccountMemberType {
  /** 企业会员 */
  Enterprise = 2,
  /** 个人会员 */
  Individual = 3,
}

/** 个人开户步骤 */
export enum ERegisterStep {
  Invalid = -1,
  /** 未建账号 */
  NotCreated = 0,
  /** 已建账号 */
  Created = 1,
  /** 已绑定手机 */
  Phone = 2,
  /** 已实名认证 */
  Name = 3,
  /** 已签协议 */
  Contract = 4,
  /** 已开户 */
  Opened = 5,
}

/** 企业开户步骤 */
export enum EEnterpriseRegisterStep {
  Invalid = -1,
  /** 未建账号 */
  NotCreated = 0,
  /** 已建账号 */
  Created = 1,
  /** 已实名认证 */
  Info = 2,
  /** 已绑定手机 */
  Phone = 3,
  /** 已签协议 */
  Contract = 4,
  /** 已开户 */
  Opened = 5,
}

export interface IAccountInfo {
  /** 账户识别id */
  id?: string;
  /** 存管类型，华通或通联 */
  depository_unit?: 'HUATONG' | 'TONGLIAN';
  /** 个人手机 */
  phone?: string;
  /** 个人姓名（或）企业名称 */
  name?: string;
  /** 认证类型，三证：1， 一证：2 */
  enterprise_auth_type?: number;
  /** 营业执照 */
  business_license?: string;
  /** 组织机构代码 */
  organization_code?: string;
  /** 税务登记证 */
  tax_registration_certificate?: string;
  /** 统一社会信用码 */
  unified_social_credit_code?: string;
  /** 法人姓名 */
  legal_name?: string;
  /** 法人手机 */
  legal_phone?: string;
  /** 对公账户 */
  public_account?: string;
  /** 银行名称 */
  bank_name?: string;
  /** 支行名称 */
  branch_bank_name?: string;
  /** 支付行号 */
  payment_bank_code?: string;
  /** 营业执照图片地址 */
  pic_business_license?: string;
  /** 组织机构代码证地址 */
  pic_organization_code_certification?: string;
  /** 税务登记证图片地址 */
  pic_tax_registration_certification?: string;
  /** 身份证正面图片地址 */
  pic_id_card_front?: string;
  /** 身份证反面图片地址 */
  pic_id_card_back?: string;
  /** 证件类型 */
  identity_type?: number;
  /** 证件号码 */
  identity_no?: string;
  /** 二维码验证 */
  verification_code?: string;
  /**
   * 当前所在注册步骤
   */
  step: ERegisterStep | EEnterpriseRegisterStep;
  /** 证件认证状态：true - 成功， false - 失败， null - pending或无效 */
  authenticate_success?: boolean | null;
  /** 开户类型 */
  member_type: EAccountMemberType;
  /** 是否为默认收款账户 */
  is_default?: boolean;
  /** 是否完成账号开户 */
  finally_success?: boolean;
  /** 证件有效期 起始日期 */
  identity_begin_date?: string;
  /** 证件有效期 截止日期 */
  identity_end_date?: string;
  address?: string;
  profession_no?: string;
  telephone?: string;
  /** 检测流程状态 */
  step_checking?: { [key: number]: boolean };
}

/**
 * 银行卡信息
 */
export interface ICardInfo {
  /** 银行卡号 */
  card_no: string;
  /** 脱敏银行号(前端处理) */
  maskCardNo: string;
  /** 银行卡类型 1-储蓄卡, 2-信用卡 */
  card_type: 1 | 2;
  /** 银行卡状态 1-已绑定, 2-已解除 */
  card_state: 1 | 2;
  /** 银行名称 */
  bank_name: string;
  /** 银行卡关联手机号  */
  phone: string;
  /** 银行logo(前端处理) */
  logo?: string;
}

/**
 * 申请绑卡提交参数
 */
export interface IApplyBindCardParam {
  /** 银行卡号 */
  card_no: string;
  /** 银行卡关联手机号 */
  phone: string;
  /** 银行卡账户名 */
  name: string;
  /** 账户证件号 */
  identity_no: string;
  /** 账户证件类型 */
  identity_type: number;
}

/**
 * 申请绑卡提交返回数据
 */
export interface IApplyBindCard {
  /** 银行卡号 */
  card_no: string;
  /** 银行卡类型 */
  card_type: 1 | 2;
  /** 银行代码 */
  bank_code: string;
  /** 银行卡名称 */
  bank_name: string;
  /** 流水号 */
  trans_no: string;
  /** 错误码 */
  code: number;
  /** 错误信息 */
  detail: string;
}

/**
 * 余额信息
 */
export interface IBalanceInfo {
  /** 剩余金额（元） */
  amount: number;
  /** 冻结金额（元） */
  amount_frozen: number;
  /** 错误码 */
  code: number;
  /** 错误信息 */
  detail: string;
}

/**
 * 确认绑卡提交参数(已废弃)
 */
// export interface IConfirmBindCardParam {
//   /** 银行卡号 */
//   card_no: string;
//   /** 银行卡关联手机号 */
//   phone: string;
// }

/**
 * 确认绑卡提交返回数据(已废弃)
 */
// export interface IConfirmBindCard {
//   /** 银行卡号 */
//   card_no: string;
//   /** 银行卡类型 */
//   card_type: 1 | 2;
//   /** 银行代码 */
//   bank_code: string;
//   /** 银行卡名称 */
//   bank_name: string;
//   /** 流水号 */
//   trans_no: string;
//   /** 错误码 */
//   code: number;
//   /** 错误信息 */
//   detail: string;
// }

/**
 * 请求提现接口参数
 */
export interface IGetCashApplyParam {
  /** 提现银行卡号 */
  card_no: string;
  /** 提现银行卡属性: 0-个人 1-企业 */
  card_prop: 0 | 1;
  /** 提现金额 */
  amount: number;
}

/**
 * 确认提现接口参数
 */
export interface IGetCashConfirmParam {
  /** 订单号 */
  order_no: string;
  /** 验证码 */
  verification_code: number;
}

/**
 * [请求|确认]提现接口返回数据
 */
export interface IGetCashRes {
  /** 支付状态 */
  pay_status: 'success' | 'pending' | 'fail';
  /** 支付失败信息 */
  pay_detail: string;
  /** 订单号 */
  order_no: string;
  /** 错误码 */
  code: number;
  /** 错误信息 */
  detail: string;
}

/**
 * 查询交易明细参数
 */
export interface IOrderSearch {
  /** 起始日期 */
  created_date__gte: string;
  /** 结束日期 */
  created_date__lte: string;
  /** 分页偏移量 */
  offset: number;
  /** 分页大小 */
  limit: number;
}

export interface IOrderResult {
  /** 组件需要的key(前端处理) */
  key: string;
  /** 备注 */
  extend_info: string;
  /** 交易号 */
  trade_no: string;
  /** 订单号 */
  order_no: string;
  /** 当前关联的账号名 */
  account_name: string;
  /** 当前余额(元) */
  amount: number;
  /** 当前冻结余额(元) */
  amount_frozen: number;
  /** 原始金额(元) */
  amount_original: number;
  /** 变更金额(元) */
  amount_changed: number;
  /** 变更时间 */
  created_time: string;
  /** 交易类型 */
  trade_type: string;
}

/**
 * 认证要素补录
 * https://yapi.macrocura.com/project/102/interface/api/2490
 */
export interface IAuthenticateElement {
  address: string;
  telephone: string;
  identity_begin_date: string;
  identity_end_date: string;
  profession_no?: string;
}

/**
 * 认证要素及影印件补录
 * https://yapi.macrocura.com/project/102/interface/api/2490
 */
export interface IAccountSupplement {
  // 华通
  authenticate_elements?: IAuthenticateElement;
  // 华通
  pictures?: {
    pic_id_card_front: string;
    pic_id_card_back: string;
    pic_business_license?: string;
  };
  // 通联
  tonglian_business_photocopy?: {
    pic_business_license: string;
    pic_organization_code_certification?: string;
    pic_tax_registration_certification?: string;
  };
  // 通联
  tonglian_id_card_photocopy?: {
    pic_id_card_front?: string;
    pic_id_card_back?: string;
  };
}

/** 照片OCR自动审核状态 */
export enum PhotoOCRState {
  /** 没上传过照片 */
  NotUpload = 'NOT_UPLOAD',
  /** 已上传, 正在OCR自动审核中 */
  OCRPending = 'OCR_PENDING',
  /** 已上传, OCR自动审核成功 */
  OCRSuccess = 'OCR_SUCCESS',
  /** 已上传, OCR自动审核失败 */
  OCRFailed = 'OCR_FAILED',
}

/** 九要素完整性结果 */
interface AccountInfoCompletion {
  authenticate_elements_ok: boolean;
  pictures_ok: boolean;
  /** 营业执照，组织机构代码张，税务登记证识别状态 */
  tonglian_business_photocopy_status: PhotoOCRState;
  /** 法人身份证识别状态 */
  tonglian_id_card_photocopy_status: PhotoOCRState;
  /** OCR识别失败原因 */
  tonglian_photocopy_ocr_failed_reason?: string;
}

/** 交易手续费 */
export interface ITransactionData {
  id: number;
  /** 类型 */
  user_type: string;
  user_uuid: string;
  /** 名称 */
  name: string;
  /** 交易手续费 */
  handling_fee_rate: number;
  uuid?: string;
  /** 是否启用提现手续费 */
  withdraw_fee_enable: boolean;
}

/**
 * 解绑通联手机号接口参数
 */
export interface IUnbindMobileParam {
  /** 手机号 */
  phone: string;
  /** 验证码 */
  verification_code: string;
}

/**
 * 企业用户信息修改数据结构
 */
export interface IAccountEditRecord {
  /** 个人姓名（或）企业名称 */
  name?: string;
  /** 企业地址 - 暂不使用 */
  address?: string;
  /** 联系电话 - 暂不使用 */
  phone?: string;
  /** 法人姓名 */
  legal_name?: string;
  /** 法人手机 */
  legal_phone?: string;
  /** 法人证件号 */
  identity_no?: string;
  /** 身份证正面图片地址 */
  pic_id_card_front?: string;
  /** 身份证反面图片地址 */
  pic_id_card_back?: string;
  /** 营业执照图片地址 */
  pic_business_license?: string;
  /** 证件有效期 起始日期 */
  identity_begin_date?: string;
  /** 证件有效期 截止日期 */
  identity_end_date?: string;
}

export interface IAccountEditRecordResult {
  // 审核单uuid
  uuid: string;
  // 申请单状态 PENDING-审核中 SUCCESS-成功 FAIL-失败
  status: 'PENDING' | 'SUCCESS' | 'FAIL';
  // 审核单字段详细	 备注: 审核单字段详细
  details: IAccountEditRecord;
  // 发生修改的字段名称
  has_modify_fields: string[];
  fail_reason: string;
}

/**
 * 脉景收款账户 service
 * @class WalletService
 */
class WalletService {
  /** 收款账户账号信息 */
  accountInfo: IAccountInfo[] = [];
  // private urlBase = 'https://yapi.macrocura.com/mock/102/account/';
  private urlBase = APIS.accountUrl('');

  /**
   * 获取收款账户账号信息
   *
   * @memberof WalletService
   */
  async getAccounts(): Promise<IAccountInfo[]> {
    try {
      const res = await HttpService.get(`${this.urlBase}accounts/`);
      this.accountInfo = !!res && isArray(res.results) ? res.results : [];
    } catch (err) {
      if (err.response && err.response.status === 404) {
        this.accountInfo = [
          {
            step: ERegisterStep.NotCreated,
            member_type: EAccountMemberType.Individual,
          },
        ];
      }
    }
    return this.accountInfo;
  }

  /**
   * 设置默认收款账号
   * @param accountId
   */
  async setDefaultAccount(accountId: string): Promise<boolean> {
    let isSucceed = false;
    const api = `${this.urlBase}default-account/`;
    try {
      await HttpService.patch(api, { id: accountId });
      isSucceed = true;
    } catch (error) {
      console.error(error);
    }
    return isSucceed;
  }

  async createAccount(memberType: EAccountMemberType): Promise<boolean> {
    const data = { step: ERegisterStep.Created, member_type: memberType };
    const res = await HttpService.post(`${this.urlBase}accounts/`, data);
    return !!res;
  }

  async registerPhone(data: IAccountInfo): Promise<boolean> {
    const res = await HttpService.post(`${this.urlBase}accounts/`, data);
    return !!res;
  }

  async registerName(data: IAccountInfo): Promise<boolean> {
    const res = await HttpService.post(`${this.urlBase}accounts/`, data);
    return !!res;
  }

  async openAccount(memberType: EAccountMemberType): Promise<boolean> {
    const data = {
      step: memberType === EAccountMemberType.Individual ? ERegisterStep.Opened : EEnterpriseRegisterStep.Opened,
      member_type: memberType,
    };
    const res = await HttpService.post(`${this.urlBase}accounts/`, data);
    return !!res;
  }

  async getContract(param: { jumpUrl: string; memberType: EAccountMemberType }): Promise<{ url: string }> {
    return HttpService.get(`${this.urlBase}sign-contract/`, param);
  }

  /**
   * [绑定|解绑]通联账号手机号验证码
   * @param phone 通联账号手机号
   * @param memberType 会员类型
   * @param bindCode [6: 解绑手机 | 9: 绑定手机]
   * @returns
   */
  async verifyCode(phone: string, memberType: EAccountMemberType, bindCode: 6 | 9 = 9): Promise<boolean> {
    const data = {
      phone,
      member_type: memberType,
      verification_code_type: bindCode,
    };
    const res = await HttpService.post(`${this.urlBase}verification-code/`, data);
    return !!res;
  }

  async getMasterBanks(): Promise<Array<{ index: string; name: string }>> {
    // US#16237要求limit从100改为500
    const res = await HttpService.get(`${this.urlBase}bank/master-banks/`, { limit: 500 });
    return res ? res.results : [];
  }

  async getBranchBanks(param: { name?: string; index: string }): Promise<Array<{ code: string; name: string }>> {
    const res = await HttpService.get(`${this.urlBase}bank/branch-banks/`, param);
    return res ? res.results : [];
  }

  /**
   * 申请绑卡
   */
  async applyBindCard(accountId: string, param: IApplyBindCardParam): Promise<IApplyBindCard | undefined> {
    let result: IApplyBindCard | undefined;
    try {
      const api = `${this.urlBase}accounts/${accountId}/cards/apply/`;
      result = await HttpService.post(api, param);
    } catch (error) {
      const errotData = error.response.data;
      let errorText = errotData.detail;
      if (!errorText) {
        errorText = '服务器报错';
      } else if (errotData.code === '10001') {
        // 针对特殊code， 特殊处理
        errorText = '请输入正确的借记卡卡号!';
      }
      message.error(errorText);
      console.error(error.response);
    }
    return result;
  }

  /**
   * 确认绑卡(已废弃)
   */
  // async confirmBindCard(accountId: string, param: IConfirmBindCardParam): Promise<IConfirmBindCard | undefined>  {
  //   let result: IConfirmBindCard | undefined;
  //   try {
  //     const api = `${this.urlBase}accounts/${accountId}/cards/`;
  //     result = await HttpService.post(api, param);
  //   } catch(error) {
  //     this.handleError(error);
  //   }
  //   return result;
  // }

  /**
   * 统一处理错误提示
   *
   */
  handleError(error: any) {
    const errorTextArray: string[] = [];
    const errorData = error.response && error.response.data;
    if (errorData) {
      const { result_msg: resultMsg, detail: errorDetail } = errorData;
      if (!errorDetail) {
        if (resultMsg) {
          errorTextArray.push(resultMsg);
        } else {
          errorTextArray.push('服务器报错');
        }
      } else if (isObject(errorDetail)) {
        for (const key in errorDetail) {
          if (errorDetail.hasOwnProperty(key)) {
            const value = errorDetail[key];
            if (isArray(value)) {
              errorTextArray.push(`[${key}]: ${value.join()}`);
            } else if (isString(value)) {
              errorTextArray.push(`[${key}]: ${value}`);
            }
          }
        }
      } else if (isString(errorDetail)) {
        errorTextArray.push(errorDetail);
      }
    }
    message.error(errorTextArray.join(' '));
    console.error(error.response);
  }

  /**
   * 解绑银行卡
   */
  async unbindBankCard(accountId: string, cardNo: string): Promise<boolean> {
    let isSucceed = false;
    try {
      const api = `${this.urlBase}accounts/${accountId}/cards/${cardNo}/`;
      await HttpService.delete(api);
      isSucceed = true;
    } catch (error) {
      this.handleError(error);
    }
    return isSucceed;
  }

  /**
   * 申请提现
   */
  async applyGetCash(accountId: string, param: IGetCashApplyParam): Promise<IGetCashRes | undefined> {
    let result: IGetCashRes | undefined;
    try {
      const api = `${this.urlBase}accounts/${accountId}/withdraw/apply/`;
      result = await HttpService.post(api, param);
    } catch (error) {
      this.handleError(error);
    }
    return result;
  }

  /**
   * 确认提现
   */
  async confirmGetCash(accountId: string, param: IGetCashConfirmParam): Promise<IGetCashRes | undefined> {
    let result: IGetCashRes | undefined;
    try {
      const api = `${this.urlBase}accounts/${accountId}/withdraw/`;
      result = await HttpService.post(api, param);
    } catch (error) {
      let errorText = error.response.data.detail;
      if (!errorText) {
        errorText = '网络不稳定，请刷新页面查看提现是否成功';
      }
      message.error(errorText);
      console.error(error.response);
    }
    return result;
  }

  /**
   * 查询余额信息
   */
  async fetchBalance(accountId: string): Promise<number> {
    let balance = 0;
    try {
      const api = `${this.urlBase}accounts/${accountId}/balance/`;
      const res: IBalanceInfo = await HttpService.get(api);
      if (!!res) {
        // 余额 = 当前总金额 - 被冻结的金额(确认提现后-钱打进卡之前)
        balance = res.amount - res.amount_frozen;
      }
    } catch (error) {
      this.handleError(error);
    }
    return balance;
  }

  /**
   * 查询企业账户修改审核状态
   */
  async fetchAccountExamine(): Promise<IAccountEditRecordResult | undefined> {
    try {
      const api = `${this.urlBase}enterprise-modifications/latest/`;
      const res: ICommonResult<IAccountEditRecordResult> = await HttpService.get(api);
      // if (!!res && res.success && !!res.data) {
      if (!!res && res.success && !!res.data) {
        return res.data;
      }
    } catch (error) {
      this.handleError(error);
    }
    return undefined;
  }

  /** 企业化更新信息 */
  async updateEnterpriseAccountInfo(params: Partial<IAccountEditRecord>): Promise<{ status: boolean; msg: string }> {
    const api = `${this.urlBase}enterprise-modifications/`;
    let status = false;
    let msg = '服务器错误，请稍后重试';
    try {
      const res: ICommonResult<{ status: 'PENDING' | 'SUCCESS' | 'FAIL'; uuid: string }> = await HttpService.post(api, params);
      if (!!res) {
        status = res.success;
        msg = res.msg;
      }
    } catch (error) {
      this.handleError(error);
    }
    return {
      status,
      msg,
    };
  }

  /**
   * 获取关联的银行卡(已绑定|已解除)
   */
  async fetchBankCards(accountId: string): Promise<ICardInfo[]> {
    let result: ICardInfo[] = [];
    try {
      const api = `${this.urlBase}accounts/${accountId}/cards/`;
      const res: ICardInfo[] = await HttpService.get(api);
      if (isArray(res)) {
        result = res;
      }
    } catch (error) {
      this.handleError(error);
    }
    return result;
  }

  /**
   * 获取交易明细记录
   */
  async fetchOrderRecords(accountId: string, param: IOrderSearch): Promise<IPageResult<IOrderResult[]> | undefined> {
    let result: IPageResult<IOrderResult[]> | undefined;
    try {
      const api = `${this.urlBase}accounts/${accountId}/orders/`;
      const res: IPageResult<IOrderResult[]> = await HttpService.get(api, param);
      if (!!res && isArray(res.results)) {
        result = res;
      }
    } catch (error) {
      this.handleError(error);
    }
    return result;
  }

  /**
   * 完善开户账号完整性: 认证要素及影印件补录
   */
  buildAccountcompletionInfo(
    memberType: EAccountMemberType,
    accountInfo: any,
    userInfo: IAgentInfo | IDrugStoreInfo | undefined
  ): IAuthenticateElement | undefined {
    let submitAddress = '';
    // 起始时间 为空 取空字符串
    const startDate = accountInfo.identity_begin_date ? (accountInfo.identity_begin_date as moment.Moment).format('YYYY-MM-DD') : '';
    // 截至时间为空 取 9999-12-31
    const endDate = accountInfo.identity_end_date
      ? (accountInfo.identity_end_date as moment.Moment).format('YYYY-MM-DD')
      : moment('9999-12-31', 'YYYY-MM-DD').format('YYYY-MM-DD');
    if (!userInfo) {
      return;
    }

    const { location, address } = userInfo;
    const locationArr: IAreaInfo[] = [];
    LocationService.getAreaChain(location, locationArr);
    locationArr.reverse();
    const locationNameArr = locationArr.map((item) => item.text);
    submitAddress = `${locationNameArr.join('')}${address}`;
    let telephone = '';
    if (accountInfo.phone) {
      telephone = accountInfo.phone || '';
    }

    if (!telephone) {
      message.error('手机号错误');
      return;
    }

    const result: IAuthenticateElement = {
      address: submitAddress,
      telephone,
      identity_begin_date: startDate,
      identity_end_date: endDate,
    };
    if (memberType === EAccountMemberType.Individual) {
      result.profession_no = '40100';
    }
    return result;
  }

  /**
   * 验证开户账号要素以及影印完整性
   */
  async fetchAccountSupplementCheck(accountId: string): Promise<AccountInfoCompletion> {
    let result: AccountInfoCompletion = {
      authenticate_elements_ok: true,
      pictures_ok: true,
      tonglian_business_photocopy_status: PhotoOCRState.OCRSuccess,
      tonglian_id_card_photocopy_status: PhotoOCRState.OCRSuccess,
    };
    try {
      const api = `${this.urlBase}accounts/${accountId}/info-completion/`;
      result = await HttpService.get(api);
    } catch (error) {
      this.handleError(error);
    }
    return result;
  }

  /** 分润设置查询 */
  async fetchHasHandlingFee(user_uuid: string): Promise<boolean> {
    try {
      const api = APIS.domainUrl(APIS.TransactionSettings);
      const res: ITransactionData[] = await HttpService.get(api, { user_uuid });
      if (!!res && Array.isArray(res) && res.length > 0) {
        return res[0].withdraw_fee_enable;
      }
    } catch (error) {
      console.error(error);
    }
    return true;
  }

  /**
   * 认证要素及影印件补录
   */
  async updateAccountSupplement(accountId: string, params: Partial<IAccountSupplement>): Promise<IAccountSupplement | undefined> {
    let result: IAccountSupplement | undefined;
    const api = `${this.urlBase}accounts/${accountId}/info-completion/`;
    try {
      result = await HttpService.post(api, params);
    } catch (error) {
      this.handleError(error);
    }
    return result;
  }

  /**
   * 解绑通联手机号
   */
  async unbindMobile(accountId: string, params: IUnbindMobileParam) {
    const api = `${this.urlBase}accounts/${accountId}/unbind-phone/`;
    let result = false;
    try {
      const res = await HttpService.post(api, params);
      if (!!res) {
        result = true;
      }
    } catch (error) {
      this.handleError(error);
    }
    return result;
  }
}

export default new WalletService();



// WEBPACK FOOTER //
// ./src/services/WalletService.ts